import axios from "axios";
import { ROOT_URL } from "../../api/ApiConfig";
import { getPlatformSession, signOutPlatformAdmin } from "./platformAuth";

const BASE = `${ROOT_URL}/platform-admin`;

function authHeaders() {
  const token = getPlatformSession()?.token;
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

/** Bearer-only — never send tenant auth cookies on platform-admin routes. */
const noCookies = { withCredentials: false };

function unwrapError(error) {
  const data = error?.response?.data;
  if (data && typeof data === "object") return data;
  return { success: false, message: error?.message || "Request failed", status: error?.response?.status };
}

async function request(fn) {
  try {
    const response = await fn();
    const payload = response?.data;
    if (payload?.status === 401) {
      signOutPlatformAdmin();
      window.location.href = "/platform-admin/login";
    }
    return payload;
  } catch (error) {
    if (error?.response?.status === 401) {
      signOutPlatformAdmin();
      window.location.href = "/platform-admin/login";
    }
    return unwrapError(error);
  }
}

export async function fetchPlatformOverview() {
  return request(() => axios.get(`${BASE}/overview`, { headers: authHeaders(), ...noCookies }));
}

export async function fetchPlatformBusinesses({
  page = 1,
  limit = 20,
  search = "",
  status = "",
  sort_by = "createdAt",
  sort_dir = "desc",
} = {}) {
  const params = { page, limit, sort_by, sort_dir };
  if (search) params.search = search;
  if (status && status !== "all") params.status = status;
  return request(() => axios.get(`${BASE}/businesses`, { headers: authHeaders(), params, ...noCookies }));
}

export async function fetchPlatformBusinessById(businessId) {
  return request(() => axios.get(`${BASE}/businesses/${businessId}`, { headers: authHeaders(), ...noCookies }));
}

export async function banPlatformBusiness(businessId, { reason }) {
  return request(() =>
    axios.post(`${BASE}/businesses/${businessId}/ban`, { reason }, { headers: authHeaders(), ...noCookies })
  );
}

export async function suspendPlatformBusiness(businessId, { reason }) {
  return request(() =>
    axios.post(`${BASE}/businesses/${businessId}/suspend`, { reason }, { headers: authHeaders(), ...noCookies })
  );
}

export async function activatePlatformBusiness(businessId) {
  return request(() =>
    axios.post(`${BASE}/businesses/${businessId}/activate`, {}, { headers: authHeaders(), ...noCookies })
  );
}

/** Normalize list payload whether `data` is the array or wraps it. */
export function parseBusinessesList(payload) {
  const raw = payload?.data;
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.businesses)
      ? raw.businesses
      : Array.isArray(raw?.items)
        ? raw.items
        : [];
  const pagination = payload?.pagination || raw?.pagination || {
    page: 1,
    limit: list.length,
    total: list.length,
    total_pages: 1,
  };
  return { list, pagination };
}

export function mapBusinessListRow(api) {
  if (!api) return null;
  const owner = api.owner || {};
  return {
    id: api._id,
    name: api.business_name || "—",
    slug: api.slug || "",
    email: api.business_email || "",
    phone: api.business_phone || "",
    website: api.business_website_link || "",
    status: (api.status || "active").toLowerCase(),
    ownerName: owner.full_name || "—",
    ownerEmail: owner.email || "",
    ownerActive: owner.is_active !== false,
    createdAt: api.createdAt,
    updatedAt: api.updatedAt,
    raw: api,
  };
}

export function mapBusinessDetail(payload) {
  const data = payload?.data || {};
  const business = data.business || {};
  const owner = data.owner || {};
  const metrics = data.metrics || {};
  return {
    id: business._id,
    name: business.business_name || "—",
    slug: business.slug || "",
    email: business.business_email || "",
    phone: business.business_phone || "",
    address: business.business_address || "",
    website: business.business_website_link || "",
    logo: business.business_logo && business.business_logo !== "null" ? business.business_logo : null,
    staffCount: business.business_staff_count || "",
    status: (business.status || "active").toLowerCase(),
    mailDomainVerified: Boolean(business.mail_domain_verified),
    ticketFromEmail: business.ticket_from_email || "",
    createdAt: business.createdAt,
    updatedAt: business.updatedAt,
    owner: {
      id: owner._id,
      name: owner.full_name || "—",
      email: owner.email || "",
      emailVerified: Boolean(owner.owner_email_verified),
      isActive: owner.is_active !== false,
      joiningDate: owner.joining_date || owner.createdAt,
    },
    metrics: {
      agentsTotal: metrics.agents?.total ?? 0,
      agentsActive: metrics.agents?.active ?? 0,
      customersTotal: metrics.customers?.total ?? 0,
      chatsOpen: metrics.chats?.open ?? 0,
      chatsTotal: metrics.chats?.total ?? 0,
      ticketsOpen: metrics.tickets?.open ?? 0,
      ticketsTotal: metrics.tickets?.total ?? 0,
    },
    raw: data,
  };
}

export function formatPlatformDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
}
